import React, { Component } from 'react';
import {
    Form,
    Input,
    InputNumber,
    Select,
    Button,
    Row,
    Col,
    Space,
    Divider,
    Alert,
    Popconfirm
} from 'antd';
import { businessUnit } from '../../../config';
import { encodeAppNativeFormat, parseAppNativeFormat } from '../adFormatUtils';

const { Option } = Select;

const AD_PLAN_OPTIONS = ['no_plan', 'advs', 'no_advs', 'avod'];
const BUSINESS_UNIT_OPTIONS = Array.isArray(businessUnit) ? businessUnit : [];

const normalizeAdPlans = (value) => {
    if (!value) {
        return [];
    }
    const arrayValue = Array.isArray(value) ? value : [value];
    return Array.from(
        new Set(
            arrayValue
                .filter(Boolean)
                .filter(plan => AD_PLAN_OPTIONS.includes(plan))
        )
    );
};

const buildNormalizedInitialValues = (initialValues = {}) => {
    const normalizedInitialValues = {
        ...(initialValues || {}),
    };

    normalizedInitialValues.adPlans = normalizeAdPlans(initialValues?.adPlans);

    if (!normalizedInitialValues.adPlacement) {
        normalizedInitialValues.adPlacement = initialValues?.adPlacement || '';
    }

    if (initialValues?.asset_id && !normalizedInitialValues.assetId) {
        normalizedInitialValues.assetId = initialValues.asset_id;
    }

    const appNativeFormat = parseAppNativeFormat(initialValues?.adFormat);
    if (appNativeFormat) {
        normalizedInitialValues.adFormat = appNativeFormat.adFormat;
        normalizedInitialValues.exitButtonTimeDelay = appNativeFormat.exitButtonTimeDelay;
    } else {
        normalizedInitialValues.exitButtonTimeDelay = 0;
    }

    return normalizedInitialValues;
};

class AdUnitForm extends Component {
    formRef = React.createRef();

    componentDidMount() {
        const { initialValues } = this.props;
        this.setFormValuesFromProps(initialValues);
    }

    componentDidUpdate(prevProps) {
        if (JSON.stringify(prevProps.initialValues) !== JSON.stringify(this.props.initialValues)) {
            this.setFormValuesFromProps(this.props.initialValues);
        }
    }

    setFormValuesFromProps = (initialValues) => {
        const normalizedInitialValues = buildNormalizedInitialValues(initialValues);

        const defaultValues = {
            status: initialValues?.status || 'submitted',
            adPlans: normalizedInitialValues.adPlans,
            ...normalizedInitialValues
        };

        this.formRef.current?.setFieldsValue(defaultValues);
    }

    handleSubmit = (values) => {
        const normalizedValues = {
            ...values,
            adPlans: normalizeAdPlans(values?.adPlans),
        };

        if (normalizedValues.adFormat === 'app_native') {
            normalizedValues.adFormat = encodeAppNativeFormat(normalizedValues.exitButtonTimeDelay);
        }
        delete normalizedValues.exitButtonTimeDelay;

        this.props.onSubmit(normalizedValues);
    };

    render() {
        const { onCancel, initialValues, isSuperAdmin } = this.props;
        const normalizedInitialValues = buildNormalizedInitialValues(initialValues);

        const isEditing = !!initialValues;
        const formInitialValues = {
            status: initialValues?.status || 'submitted',
            adPlans: normalizedInitialValues.adPlans,
            ...normalizedInitialValues
        };

        return (
            <div className="adunit-form">
                <Alert
                    message="Important Notice"
                    description="Once creation is submitted, status will be converted to Submitted for reviewing the changes first"
                    type="info"
                    showIcon
                    style={{ marginBottom: 24 }}
                />

                <Form
                    ref={this.formRef}
                    layout="vertical"
                    onFinish={this.handleSubmit}
                    initialValues={formInitialValues}
                >
                    <Row gutter={[16, 0]}>
                        <Col xs={24}>
                            <Form.Item
                                label="Asset ID"
                                name="assetId"
                                rules={isSuperAdmin ? [{ message: 'Please enter asset ID', }] : []}
                            >
                                <Input
                                    placeholder="System Auto Generate"
                                    disabled={true}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Ad Unit Name"
                                name="name"
                                rules={[
                                    { required: true, min: 3, message: 'Name must be at least 3 characters' }
                                ]}
                            >
                                <Input placeholder="Enter ad unit name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Platform"
                                name="platform"
                                rules={[{ required: true, message: 'Please select platform' }]}
                            >
                                <Select placeholder="Select platform">
                                    <Option value="web">Web</Option>
                                    <Option value="ios">iOS</Option>
                                    <Option value="android">Android</Option>
                                    <Option value="huawei">Huawei</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Business Unit"
                                name="businessUnit"
                                rules={[{ required: true, message: 'Please select business unit' }]}
                            >
                                <Select placeholder="Select business unit">
                                    {BUSINESS_UNIT_OPTIONS.map(unit => (
                                        <Option key={unit} value={unit}>
                                            {unit}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Ad Format"
                                name="adFormat"
                                rules={[{ required: true, message: 'Please select ad format' }]}
                            >
                                <Select placeholder="Select ad format">
                                    <Option value="banner">Banner</Option>
                                    <Option value="interstitial">Interstitial</Option>
                                    <Option value="interstitial_backup">Interstitial Backup</Option>
                                    <Option value="instream_video">Instream Video</Option>
                                    <Option value="web_native">Web Native</Option>
                                    <Option value="app_native">App Native</Option>
                                    <Option value="native_3adbanner">native_3adbanner</Option>
                                    <Option value="app_open">App Open</Option>
                                    <Option value="rewarded">Rewarded</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        noStyle
                        shouldUpdate={(previousValues, currentValues) => previousValues.adFormat !== currentValues.adFormat}
                    >
                        {({ getFieldValue }) => getFieldValue('adFormat') === 'app_native' && (
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={{ span: 12, offset: 12 }}>
                                    <Form.Item
                                        label="Exit Button Time Delay (seconds)"
                                        name="exitButtonTimeDelay"
                                        extra="Use 0 to show the exit button immediately."
                                        rules={[
                                            { required: true, message: 'Please enter the exit button time delay' },
                                            {
                                                type: 'integer',
                                                min: 0,
                                                message: 'Delay must be a non-negative whole number',
                                            },
                                        ]}
                                    >
                                        <InputNumber
                                            min={0}
                                            step={1}
                                            placeholder="0"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </Form.Item>

                    <Row gutter={[16, 0]}>
                        <Col xs={24}>
                            <Form.Item
                                label="Ad Placement"
                                name="adPlacement"
                                rules={[
                                    { max: 120, message: 'Placement must be 120 characters or less' }
                                ]}
                            >
                                <Input placeholder="e.g. Homepage header slot" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24}>
                            <Form.Item
                                label="Ad Plans"
                                name="adPlans"
                                extra="Select all plans that apply to this ad unit."
                                normalize={(value) => normalizeAdPlans(value)}
                            >
                                <Select
                                    mode="tags"
                                    placeholder="Select ad plans"
                                    options={AD_PLAN_OPTIONS.map(plan => ({
                                        value: plan,
                                        label: plan.replace(/_/g, ' '),
                                    }))}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Ad Type"
                                name="adType"
                                rules={[{ required: true, message: 'Please select ad type' }]}
                            >
                                <Select placeholder="Select ad type">
                                    <Option value="mature">Mature</Option>
                                    <Option value="leisure">Leisure</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        {
                            isSuperAdmin ?
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label="Status"
                                        name="status"
                                        rules={[{ required: true, message: 'Please select ad unit status' }]}
                                    >
                                        <Select
                                            placeholder="Select status"
                                        >
                                            <Option value="submitted">Submitted</Option>
                                            <Option value="active">Active</Option>
                                            <Option value="revoked">Revoked</Option>
                                            <Option value="rejected">Rejected</Option>
                                            <Option value="deleted">Deleted</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                :
                                <Col xs={24} md={8}>
                                    <Form.Item
                                        label="Status"
                                        name="status"
                                        rules={[{ required: true, message: 'Please select ad unit status' }]}
                                    >
                                        <Select
                                            placeholder="Select status"
                                            disabled
                                        >
                                            <Option value="active">Active</Option>
                                            <Option value="revoked">Revoked</Option>
                                            <Option value="deleted">Deleted</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                        }
                        {isSuperAdmin && (
                            <Col xs={24} md={8}>
                                <Form.Item
                                    label="Ad Partner"
                                    name="adPartner"
                                    rules={[{ required: true, message: 'Please select ad unit parter' }]}
                                >
                                    <Input
                                        placeholder="Ad partner name (managed by Kande reviewer)"
                                    />
                                </Form.Item>
                            </Col>
                        )}
                    </Row>

                    {isSuperAdmin && (
                        <>
                            <Row gutter={[16, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Ad Partner App ID"
                                        name="adPartnerAppId"
                                        extra="Pangle app_id; editable by superadmin only"
                                        rules={[
                                            { required: true, max: 80, message: 'App ID must be 80 characters or less' }
                                        ]}
                                    >
                                        <Input
                                            placeholder="Enter partner app ID"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Ad Partner Unit Path"
                                        name="adPartnerUnitPath"
                                        extra="Path used for fetching ads from GAM; superadmin only"
                                        rules={[
                                            { required: true }
                                        ]}
                                    >
                                        <Input
                                            placeholder="Enter partner unit path"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item
                                label="Ad Partner Unit ID"
                                name="adPartnerUnitId"
                                extra="Ad partner's unit id. Can only be managed by Kande reviewer"
                                rules={[
                                    { required: true, message: 'Please enter ad partner unit id' }
                                ]}
                            >
                                <Input
                                    placeholder="Partner unit ID"
                                />
                            </Form.Item>
                        </>
                    )}

                    <Divider />

                    <Row justify="end">
                        <Col>
                            <Space>
                                <Button onClick={onCancel}>
                                    Cancel
                                </Button>
                                {
                                    !isSuperAdmin &&
                                    <>
                                        <Popconfirm
                                            title={<span>This action will delete the ad unit and cannot be reverted.</span>}
                                            onConfirm={() => this.handleSubmit({ assetId: formInitialValues.assetId, status: 'deleted', adPlans: formInitialValues.adPlans })}
                                            okText="Yes"
                                            cancelText="No"
                                        >
                                            <Button color="danger" variant="solid">
                                                To Delete
                                            </Button>
                                        </Popconfirm>
                                        {
                                            formInitialValues.status !== 'revoked' ?
                                                <Popconfirm
                                                    title={<span>This action will disable the ad unit.</span>}
                                                    onConfirm={() => this.handleSubmit({ assetId: formInitialValues.assetId, status: 'revoked', adPlans: formInitialValues.adPlans })}
                                                    okText="Yes"
                                                    cancelText="No"
                                                >
                                                    <Button color="cyan" variant="solid">
                                                        To Revoke
                                                    </Button>
                                                </Popconfirm>
                                                :
                                                <Popconfirm
                                                    title={<span>This action will re-submit the ad unit.</span>}
                                                    onConfirm={() => this.handleSubmit({ assetId: formInitialValues.assetId, status: 'submitted', adPlans: formInitialValues.adPlans })}
                                                    okText="Yes"
                                                    cancelText="No"
                                                >
                                                    <Button color="cyan" variant="solid">
                                                        To Submit
                                                    </Button>
                                                </Popconfirm>
                                        }
                                    </>
                                }
                                <Button type="primary" htmlType="submit">
                                    {isEditing ? 'Update Ad Unit' : 'Create Ad Unit'}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </div>
        );
    }
}

export default AdUnitForm;
